/**
 * Quote Availability Web Component
 * Handles quote request form submission for new equipment.
 * Simplified version of RentalAvailability — no location or date logic.
 */
class QuoteAvailability extends HTMLElement {
  connectedCallback() {
    this._abort = new AbortController();
    this.bindEvents();
  }

  disconnectedCallback() {
    this._abort?.abort();
    this._abort = null;
  }

  get productId() {
    return this.dataset.productId || "";
  }

  get endpoint() {
    return this.dataset.endpoint || "";
  }

  get modalId() {
    return `quote-request-modal-${this.productId}`;
  }

  get templateId() {
    return `quote-request-modal-tpl-${this.productId}`;
  }

  get quoteForm() {
    if (!this._modal?.isConnected) return null;
    return this._modal.querySelector("form");
  }

  bindEvents() {
    this.addEventListener(
      "click",
      (e) => {
        const openBtn = e.target.closest(".js-quote-open-modal");
        if (openBtn) this.openQuoteModal(openBtn);
      },
      { signal: this._abort.signal },
    );

    // Listen for form submission
    document.addEventListener(
      "submit",
      (e) => {
        if (e.target.id === `quote-request-form-${this.productId}`) {
          this.handleFormSubmit(e);
        }
      },
      { signal: this._abort.signal },
    );

    // Listen for input changes to clear field errors
    this.bindFormInputListeners();
  }

  /**
   * Lazy mount keeps the modal out of the DOM (and any sticky parent's stacking context) until first open.
   */
  openQuoteModal(opener) {
    if (this._modal?.isConnected) {
      this._modal.open(opener);
      return;
    }

    const template = document.getElementById(this.templateId);
    if (!(template instanceof HTMLTemplateElement)) return;

    const fragment = template.content.cloneNode(true);
    const modal = fragment.querySelector("modal-dialog");
    if (!modal) return;

    document.body.appendChild(fragment);
    this._modal = modal;

    // First mount only — double rAF so the no-[open] state paints first; otherwise the browser collapses both states into one frame and skips the open transition.
    requestAnimationFrame(() => requestAnimationFrame(() => modal.open(opener)));
  }

  bindFormInputListeners() {
    const formId = `quote-request-form-${this.productId}`;

    const handleFieldEdit = (e) => {
      const field = e.target;
      if (
        !(
          field instanceof HTMLInputElement ||
          field instanceof HTMLSelectElement ||
          field instanceof HTMLTextAreaElement
        )
      )
        return;

      const form = field.closest("form");
      if (!form || form.id !== formId) return;

      const fieldGroup = field.closest(".field-group");
      if (!fieldGroup?.classList.contains("has-error")) return;

      if (field.value.trim() && this.isFieldValid(field)) {
        fieldGroup.classList.remove("has-error");
        field.removeAttribute("aria-invalid");
      }
    };

    document.addEventListener("input", handleFieldEdit, {
      signal: this._abort.signal,
    });
    document.addEventListener("change", handleFieldEdit, {
      signal: this._abort.signal,
    });
  }

  async handleFormSubmit(e) {
    e.preventDefault();

    if (this._submitting) return;
    this._submitting = true;

    const form = e.target;
    const submitBtn = form.querySelector('button[type="submit"]');

    if (!this.validateForm(form)) {
      this._submitting = false;
      return;
    }

    if (!this.endpoint) {
      console.warn('[quote-availability] Theme setting "rental_inquiry_endpoint" is empty — form submissions will fail.');
      this._submitting = false;
      this.handleFormError('Configuration error: submission endpoint is not set. Please contact the store administrator.');
      return;
    }

    this.setFormLoading(submitBtn, true);

    try {
      const formData = new FormData(form);
      const raw = Object.fromEntries(formData.entries());

      const payload = {
        customerName: [raw.first_name, raw.last_name].filter(Boolean).join(" "),
        email: raw.email,
        ...(raw.phone ? { phone: raw.phone } : {}),
        address: raw.address,
        postalCode: raw.postal_code,
        productTitle: raw.product_title,
        productSku: raw.product_sku,
        productHandle: raw.product_handle,
        ...(raw.location_name ? { location: raw.location_name } : {}),
        ...(raw.notes ? { notes: raw.notes } : {}),
        locale: (raw.locale || "en").slice(0, 2),
        formType: "quote",
      };

      const response = await fetch(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        this.handleFormSuccess();
      } else {
        const errorMsg =
          form.dataset.errorSubmit ||
          "Failed to submit request. Please try again.";
        this.handleFormError(errorMsg);
      }
    } catch (error) {
      console.error("[Quote] Submit error:", error);
      const errorMsg =
        form.dataset.errorNetwork || "Network error. Please try again.";
      this.handleFormError(errorMsg);
    } finally {
      this._submitting = false;
      this.setFormLoading(submitBtn, false);
    }
  }

  validateForm(form) {
    const requiredFields = form.querySelectorAll("[required]");
    let isValid = true;
    let firstInvalid = null;

    requiredFields.forEach((field) => {
      const fieldGroup = field.closest(".field-group");
      const errorEl = fieldGroup?.querySelector("[data-field-error] span");
      const invalid = !field.value.trim() || !this.isFieldValid(field);

      if (invalid) {
        isValid = false;
        if (!firstInvalid) firstInvalid = field;
        fieldGroup?.classList.add("has-error");
        field.setAttribute("aria-invalid", "true");
        if (errorEl) {
          errorEl.textContent = this.getFieldErrorMessage(field);
        }
      } else {
        fieldGroup?.classList.remove("has-error");
        field.removeAttribute("aria-invalid");
      }
    });

    if (firstInvalid) {
      firstInvalid.focus();
    }

    return isValid;
  }

  isFieldValid(field) {
    const value = field.value.trim();
    if (!value) return false;

    switch (field.type) {
      case "email":
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
      case "tel": {
        const digits = value.replace(/\D/g, "");
        return digits.length >= 7;
      }
      default:
        return true;
    }
  }

  getFieldErrorMessage(field) {
    const form = field.closest("form");
    const value = field.value.trim();

    if (!value) {
      return form?.dataset.errorRequired || "This field is required";
    }

    switch (field.type) {
      case "email":
        return form?.dataset.errorEmail || "Please enter a valid email address";
      case "tel":
        return form?.dataset.errorPhone || "Please enter a valid phone number";
      default:
        return form?.dataset.errorRequired || "This field is required";
    }
  }

  setFormLoading(btn, loading) {
    if (btn) {
      btn.disabled = loading;
      btn.classList.toggle("is-loading", loading);
    }
  }

  handleFormSuccess() {
    const modal = this._modal;
    if (modal?.hide) {
      modal.hide();
    } else if (modal?.close) {
      modal.close();
    }

    this.classList.add("submitted");

    document.dispatchEvent(
      new CustomEvent("quote:request-submitted", {
        detail: { productId: this.productId },
      }),
    );
  }

  handleFormError(message) {
    const form = this.quoteForm;
    if (!form) return;

    const errorContainer = form.querySelector(".js-form-error-message");
    if (!errorContainer) return;

    errorContainer.textContent = message;
    errorContainer.hidden = false;
    errorContainer.scrollIntoView({ behavior: "smooth", block: "nearest" });

    setTimeout(() => {
      errorContainer.hidden = true;
    }, 5000);
  }
}

if (!customElements.get("quote-availability")) {
  customElements.define("quote-availability", QuoteAvailability);
}
